'use strict';

import MlbTeamData from 'mlb-team-data';
import BamLinker from 'bam-linker';
import FunctionOverrides from './FunctionOverrides';
import BamArgValidator from 'bam-arg-validator';
import MlbTbdTeamToLeagueMap from 'mlb-tbd-team-to-league-map';
import moment from 'moment-timezone';
const Helpers = BamLinker.getHelpers();
const cloneDeep = MlbTeamData.getHelpers().cloneDeep;

const generatorConstructorOptions = {
    buildLinkFn: FunctionOverrides.buildLink,
    buildPlayerPageLinkFn: FunctionOverrides.buildPlayerPageLink,
    buildPlayerSlugFn: FunctionOverrides.buildPlayerSlug,
    buildTeamHomepageLinkFn: FunctionOverrides.buildTeamHomepageLink,
    buildTeamScheduleLinkFn: FunctionOverrides.buildTeamScheduleLink,
    // the required options, which we derive later:
    langCode: null,
    deviceType: null,
    teamId: null,
    appView: null
};

// game.gamedayType values that should not link to gameday
const HIDDEN_GAMEDAY_TYPES = {
    N: true,
    L: true,
    S: true
};

const AL_ID = 103;
const NL_ID = 104;
const AL_ASG_TEAM_ID = 159;
const NL_ASG_TEAM_ID = 160;

/**
 * MLB-specific subclass of BamLinker extended to support MiLB.  Returns team-homepage links, player-page links, and
 * generic links.  This BamLinker subclass is different from others, since it must work for BANE pages which live
 * within the context of a larger MLB.com, all of which has not yet been converted to BANE.
 *
 * In order to use the MlbLinker in the MiLB context, pass a truthy value for isMilb in your options object.
 * E.g: window.milbLinker = new MlbLinker(window, { isMilb: true });
 *
 * Thus, for example,
 * player-page links are fully qualified, as they currently still live on the v2/responsive site.  So it can generate
 * these fully qualified URLs, this class knows how to build both team-specific and league-level hostnames for both
 * JSP/wired (e.g. http://newyork.yankees.mlb.com) and v2/responsive (e.g. http://m.yankees.mlb.com) sites.
 *
 * Also, what this class defines as a player "slug" is "12345678/first-last" (or possibly
 * just "12345678"); in BANE style, a "slug" always contains the player ID, where as on the legacy MLB sites, "slug"
 * more likely referred to just "first-last".
 *
 * Implementation note: most of the methods in this class (and all of the methods which override methods in
 * BamLinker) are just calls to their supermethods (with all of the MLB-specific behavior coming from the way
 * we call the superconstructor and the MlbTeamData constructor - different options, different behavior - not from
 * differing implementations of the overriding methods).  As such,
 * their "implementations" (just supermethod calls) don't need to be in this class at all.  They're here, though, so we
 * can JSDoc them -
 * in most cases, the difference between MLB-specific behavior and property-agnostic behavior is not the default
 * behavior.
 *
 * @extends BamLinker
 */
class MlbLinker extends BamLinker {

    /**
     * @param windowOrReq A `window` or a BANE-controller `req`; optional - if not passed, a browser window object will
     *   be assumed to be the context.
     * @param options Optional; supported options:
     *   - langCodeOverride: a string overriding the default langCode (which is taken from the context).
     */
    constructor(windowOrReq, options = {}) {
        let genOpts = cloneDeep(generatorConstructorOptions);

        // TEMP:
        genOpts.fqSiteCorePrefixUrl = '/site-core';

        // We use this generic BANE method to derive and add appView, langCode, deviceType, and teamId; should the need
        // arise in future for different behavior, we'd switch to MLB-specific logic.

        let ctx = windowOrReq;
        if (!ctx) {
            if (!(!window)) {
                ctx = window;
            } else {
                throw new Error('windowOrReq was null or undefined, and no window object was found to use by default.');
            }
        }

        Helpers.addRequestSpecificOptions(genOpts, ctx, true);

        if (options.langCodeOverride) {
            genOpts.langCode = options.langCodeOverride;
        }

        super(
            options.isMilb ? {} : new MlbTeamData(
                windowOrReq,
                []
            ),
            genOpts
        );

        if (this._clientSide) {
            this._metaTags = windowOrReq.document.getElementsByTagName('meta');
        }

        if (options.isMilb) {
            _setMilbProperties(this, windowOrReq, options);
        }

        this._siteCoreLogosRoot = this.getFqPathToSiteCoreAssets() + '/images/logos/';
    }

    /**
     * Return a link to another BANE page, preserving language, app-view, and team (if keepTeam is true). _Use
     * `getLegacyLink()` in this class to build links to JSP/wired or v2/responsive pages._
     *
     * Note: please use this method only when there isn't a link-type-specific method (e.g.,
     * `getPlayerPageLink()`) that meets your needs.
     *
     * @param {string} urlSegment e.g. /schedule; should never contain app-view or lang path segments (/fr, /app, etc.).
     * Forward-slash will be prepended if not passed.
     * @param {boolean} keepTeam If true, the team-slug URL segment (if any) will be preserved. Default is true.
     * @return {string}
     */
    getLink(urlSegment, keepTeam) {

        return super.getLink(urlSegment, keepTeam);
    }

    /**
     * Return a URL in one of two legacy formats: "jsp" (http://newyork.yankees.mlb.com/fan_forum/index.jsp?c_id=nyy) or
     * "v2" (http://m.yankees.mlb.com/roster).
     *
     * Note: please use this method only when there isn't a link-type-specific method (e.g.,
     * `getPlayerPageLink()`) that meets your needs.
     *
     * Note: By default the langCode will be removed from the link.
     *       If you want to add the lang code back, please use `legacyLinkInsertLang`
     *
     * @param {string} urlSegment e.g. /scoreboard; should never contain app-view or lang path segments (/fr, /app,
     * etc.). Forward-slash will be prepended if not passed.
     * @param {boolean} keepTeam True to stay on this team's site (if we're on one to begin with), false to go
     *   to the league site.  Required.
     * @param {'jsp' | 'v2'} siteType One of 'jsp' or 'v2'. Required.
     * @param {string} alternateBaseUrl A URL (never with a trailing forward-slash) to use as the start of the URL
     *   instead of the default (either, e.g.,
     *   http://newyork.yankees.mlb.com or http://mlb.mlb.com (JSP) or http://m.yankees.mlb.com or http://m.mlb.com
     *   (v2).  For example, getGamedayLink() needs to link to a (sort-of) origin server, www.mlb.com, as opposed to
     *   mlb.mlb.com; thus it passes "http://www.mlb.com".
     * @return {string}
     */
    getLegacyLink(urlSegment, keepTeam, siteType, alternateBaseUrl) {
        BamArgValidator.validateArgs(arguments, ['urlSegment', 'keepTeam', 'siteType', 'alternateBaseUrl'],
            ['string', 'boolean', 'string', 'string'], [true, true, true, false]);
        let baseUrl;
        let teamId = (keepTeam ? this._teamId : 0);
        let tricode = this._teamData.getTeamTricodeFromId && this._teamData.getTeamTricodeFromId(teamId);
        if (alternateBaseUrl) {
            if (alternateBaseUrl.charAt(alternateBaseUrl.length - 1) === '/') {
                throw new Error('alternateBaseUrl ends with a forward-slash; this is not allowed.');
            }
            baseUrl = alternateBaseUrl;
        } else {
            baseUrl = this._teamData[siteType === 'jsp'
                ? 'getBaseJspUrl'
                : 'getBaseV2Url'](teamId);
        }

        // we'll see how this works - or if it matters; may need more involved logic:
        let url = baseUrl + this.getLink(urlSegment, false);
        if (siteType === 'jsp' && teamId !== 0) {
            if (url.indexOf('?') === -1) {
                url += '?';
            } else {
                url += '&';
            }
            url += 'c_id=' + tricode;
        }

        return MlbLinker.stripLanguagePathElement(url);
    }

    /**
     * Generate a gameday link for the specified game, in the specified mode (wrap, preview, etc.), for example:
     * http://www.mlb.com/r/game?gid=2016_08_16_bosmlb_balmlb_1&mode=wrap&sport_code=mlb (with "wrap" being the `mode`
     * param in this example).
     * @param statsapiGame A Statsapi game node returned by, e.g., the Schedule API
     * @param {boolean} ignoreGameState=false If true, return gameday link for final or suspended games
     * @return {string} A gameday link or null
     */
    getGamedayLink(statsapiGame, ignoreGameState = false) {

        BamArgValidator.validateStatsapiGame(statsapiGame);

        let { status = {}, gamedayType } = statsapiGame;

        if (!ignoreGameState) {

            if (status.abstractGameCode === 'F' || status.codedGameState === 'S') {

                return null;

            }

        }

        if (HIDDEN_GAMEDAY_TYPES[gamedayType]) {

            return null;

        }

        return this.getLink('/gameday/' + statsapiGame.gamePk, false);

    }

    /**
     * If a recap article exists, provide a wrap link
     * Once https://jira.mlbam.com/browse/SDBBD-2213 is complete, check for wrap article
     * @param statsapiGame
     * @return {string}
     */
    getWrapLink(statsapiGame, sId) {

        BamArgValidator.validateStatsapiGame(statsapiGame);
        let { content = {}, season } = statsapiGame;

        if (content.summary) {
            const { hasRecapArticle, hasWrapArticle } = content.summary;

            if (hasRecapArticle || hasWrapArticle) {
                return this._isMilb ?
                    this.getStatsLink(statsapiGame, sId) :
                    this.getLink(`/gameday/${statsapiGame.gamePk}/final/wrap`, false);
            }

        }

        return null;

    }

    /**
     * If a game is in pregame, return a gameday preview link, otherwise null
     * If a plain gameday link is available, don't return a preview link
     * @param statsapiGame
     * @return {string}
     */
    getPreviewLink(statsapiGame) {
        let url;

        BamArgValidator.validateStatsapiGame(statsapiGame);

        if (statsapiGame.status) {

            let code = statsapiGame.status.abstractGameCode;

            if (code && code === 'P') {

                let gameDayLink = this.getGamedayLink(statsapiGame);

                if (!gameDayLink) {
                    return this.getLink(`/gameday/${statsapiGame.gamePk}/preview`, false);
                }

            }

        }

        return null;
    }

    /**
     * Return a gameday deep link, otherwise null
     * @param statsapiGame
     * @return {string}
     */
    getDeviceDeepLink(statsapiGame) {

        BamArgValidator.validateStatsapiGame(statsapiGame);

        if (statsapiGame.gamePk) {

            return `mlbatbat://game?gamepk=${statsapiGame.gamePk}`;

        }

        return null;
    }

    /**
     * If a game is final (and not postponed or canceled) return a gameday boxscore link, otherwise null
     * @param statsapiGame
     * @return {string}
     */
    getBoxscoreLink(statsapiGame, validateGame = true) {
        const url = this.getLink(`/gameday/${statsapiGame.gamePk}/final/box`, false);

        if (validateGame) {
            BamArgValidator.validateStatsapiGame(statsapiGame);

            if (statsapiGame.status) {

                let code = statsapiGame.status.abstractGameCode;

                if (code && code === 'F') {

                    let statusCode = statsapiGame.status.statusCode;
                    let firstLetter = statusCode && statusCode.charAt(0) || '';

                    // Don't link to boxscore for postponed or canceled games (SSSS-3725)
                    if (firstLetter && firstLetter !== 'D' && firstLetter !== 'C') {
                        return url;
                    }
                }
            }
        } else {

            if (statsapiGame && statsapiGame.gamePk) {
                return url;
            }

        }

        return null;

    }

    /**
     * If a game is live, return box score link (MiLB Complex Leagues // gamedayType === 'N')
     * @param statsapiGame
     * @return {string}
     */
    getLiveBoxLink(statsapiGame) {
        let { status = {}, gamePk, gamedayType } = statsapiGame;

        if (gamedayType === 'N' && gamePk && status.abstractGameCode === 'L') {
            return this.getLink('/gameday/' + gamePk, false);
        }

        return null;
    }

    /**
     * Generate a press-notes link such as http://mlb.com/mlb/presspass/gamenotes.jsp?c_id=mlb. While press-notes links
     * are currently game-agnostic, this method takes a game node for future possible compatibility, which shouldn't
     * be an issue since wherever press-notes links appear, other links will appear as well which will require a
     * `statsapiGame`.
     * @param statsapiGame A Statsapi game node returned by, e.g., the Schedule API
     * @return {string} A press-notes link
     */
    getPressNotesLink(statsapiGame) {
        BamArgValidator.validateStatsapiGame(statsapiGame);
        return 'http://mlb.com/mlb/presspass/gamenotes.jsp?c_id=mlb';
    }

    /**
     * Generates a tickets link based on game; whether user is desktop or mobile; what the current team is (we don't
     * yet handle league sites here, just club sites); and what types of links are available from statsapi (e.g., if
     * there's no mobile-specific link, we return the desktop link, etc.).
     * @param statsapiGame A Statsapi game node returned by, e.g., the Schedule API
     * @return {string}
     */
    getTicketsLink(statsapiGame) {
        return _getTicketsLink(statsapiGame, arguments.length > 1 ? arguments[1] : false, this, 'any');
    }

    /**
     * Generates a tickets link for club schedukle app view based on game;
     * @param statsapiGame A Statsapi game node returned by, e.g., the Schedule API
     * @return {string}
     */
    getAppViewTicketLink(statsapiGame) {
        const {gamePk, teams} = statsapiGame || {};
        if (!gamePk || !teams) {
            return null;
        }

        let teamId = teams && teams.home && teams.home.team && teams.home.team.id;

        if (!teamId || typeof(teamId) !== 'number') {
            return null;
        }

        let teamslug = (this._isMilb) ? (this._teamSlugs && this._teamSlugs[teamId]) : this._teamData.getTeamSlug && this._teamData.getTeamSlug(teamId);
        return (teamslug) ? this.getLink(`/${teamslug}/tickets?game_pk=${gamePk}`, false) : null;
    }

    /**
     * Generates a tickets link based on game; base on appView we call different functions
     * @param statsapiGame A Statsapi game node returned by, e.g., the Schedule API
     * @return {string}
     */
    getClubScheduleTicketLink(statsapiGame) {
        if (this._isMilb) {
            return this.getTicketsLink(statsapiGame);
        }
        return (this._appView === 'web') ? this.getTicketsLinkForContextTeam(statsapiGame) : this.getAppViewTicketLink(statsapiGame);
    }

    /**
     * Generates a tickets link based on game; base on appView we call different functions
     * @param statsapiGame A Statsapi game node returned by, e.g., the Schedule API
     * @return {string}
     */
    getClubScheduleTicketLinkNotForContextTeam(statsapiGame) {
        return (this._appView === 'web') ? this.getTicketsLinkNotForContextTeam(statsapiGame) : this.getAppViewTicketLink(statsapiGame);
    }

    /**
     * Returns a ticket link IF AND ONLY IF there is one available which will take the user to a site where they can
     * purchase tickets from the context team (the team whose site they're visiting).  This method is meant to be used
     * in conjunction with getTicketsLinkNotForContextTeam(), which returns the "opposite" of what this method returns:
     * only non-context-team ticket links.
     *
     * This method (and its cousin getTicketsLinkNotForContextTeam()) are meant to be used instead of getTicketsLink()
     * when the consumer must know whether they're dealing with a context-team link or an opponent-team link (since in
     * some cases, we alert the user to the fact that they'll be purchasing tickets from the opponent if they proceed.
     * Expected use" consumer calls this method first; if it returns null, they call getTicketsLinkNotForContextTeam()
     * and offer the return value (if not null ofc) to the user, but telling them this is the opponent's link.
     * @param statsapiGame
     * @return {string} A url, or null if a ticket link meeting the criteria described above is not available
     */
    getTicketsLinkForContextTeam(statsapiGame) {
        return _getTicketsLink(statsapiGame, arguments.length > 1 ? arguments[1] : false, this, 'contextOnly');
    }

    /**
     * @see #getNGETicketsLinkForContextTeam - where that method returns links to buy tickets from the context team, this
     *   method returns links to buy tickets from the opponent team.
     * @param statsapiGame
     */
    getNGETicketsLinkForContextTeam(statsapiGame) {
        return _getTicketsLink(statsapiGame, arguments.length > 1 ? arguments[1] : false, this, 'any', true);
    }

    /**
     * @see #getTicketsLinkForContextTeam - where that method returns links to buy tickets from the context team, this
     *   method returns links to buy tickets from the opponent team.
     * @param statsapiGame
     */
    getTicketsLinkNotForContextTeam(statsapiGame) {
        return _getTicketsLink(statsapiGame, arguments.length > 1 ? arguments[1] : false, this, 'opponentOnly');
    }

    /**
     * Private method which returns a link to audio or video for this game (home media for home team, away media for
     * away team), or null if there is none
     * @param statsapiGame
     * @param {string} mediaTitle E.g. "MLBTV" for MLB TV, "Audio" for audio
     * @param {boolean} matchLanguage Optional; default is true. True to return a link only if one is found for the
     *   user's language; false to return links for any language.
     * @return {string}
     * @private
     */
    _getMediaLink(statsapiGame, mediaTitle, matchLanguage = true, slugOption = true) {
        const lcMediaTitle = mediaTitle.toLowerCase();
        let mediaLink = null;
        let teamslug = this._teamData.getTeamSlug && this._teamData.getTeamSlug(this._teamId);

        if (statsapiGame.content && statsapiGame.content.media && statsapiGame.content.media.epg) {
            const epgs = statsapiGame.content.media.epg;
            let lcHomeAway = '';

            if (this._teamId) {
                lcHomeAway = (statsapiGame.teams.home.team.id === this._teamId ? 'home' : 'away');
            }

            const eLength = epgs.length;

            for (let eIdx = 0; eIdx < eLength; eIdx++) {

                const epg = epgs[eIdx];

                if (epg.title.toLowerCase() === lcMediaTitle && epg.items) {

                    const items = epg.items;
                    const iLength = items.length;

                    for (let iIdx = 0; iIdx < iLength; iIdx++) {
                        const item = items[iIdx];
                        const lcMediaFeedType = (item.type || item.mediaFeedType || '').toLowerCase();
                        const language = item.language ? item.language.toLowerCase() : '';
                        const isMediaOnOrArchive = item.mediaState === 'MEDIA_ON' || item.mediaState === 'MEDIA_ARCHIVE';
                        const isValidMediaFeedType = lcMediaFeedType === 'home' || lcMediaFeedType === 'away' || lcMediaFeedType === 'national';

                        if ((!matchLanguage || language === this._langCode)
                            && (isMediaOnOrArchive)
                            && (!item.youtube || (item.youtube && item.mediaState !== 'MEDIA_ON'))
                            && (!item.facebook || (item.facebook && item.mediaState !== 'MEDIA_ON'))) {

                            const gamePk = statsapiGame.gamePk || '';
                            const isAudio = lcMediaTitle === 'audio';
                            const audioQueryStr = isAudio ? '?mediatype=audio' : '';
                            const teamslugStr = (teamslug) ? '/' + teamslug : '';
                            const gameDate = statsapiGame.gameDate || '';
                            const now = moment(gameDate).tz('America/New_York').format('YYYYMMDD');

                            if (gamePk) {
                                mediaLink = `${slugOption ? teamslugStr : ''}/tv/g${gamePk}${gameDate ? `/${now}` : ''}${audioQueryStr}`;
                            } else {
                                mediaLink = '';
                                console.error('MlbLinker: _getMediaLink: Missing gamepk or contentId');
                            }

                            if (isValidMediaFeedType) {
                                return mediaLink;
                            }
                        }
                    }
                }
            }
        }

        return mediaLink;
    }

    /**
     *
     * @param statsapiGame A Statsapi game node returned by, e.g., the Schedule API
     * @param {boolean} currentLanguageOnly True to match the user's language, false to try to match it but return
     *    a different language if user's language is not found. Optional; default is false.
     * @return An audio URL, or null if none found
     */
    getAudioLink(statsapiGame, currentLanguageOnly) {
        if (this._isMilb) {
            const platform = statsapiGame || null;
            return this._getMilbAudioLink(statsapiGame);
        }

        let url = this._getMediaLink(statsapiGame, 'Audio', true);
        if (!url && !currentLanguageOnly) {
            url = this._getMediaLink(statsapiGame, 'Audio', false);
        }
        return url;
    }

    /**
     * Milb override for getAudioLink function.
     * @param {string} platform Optional platform string (ios, android) for device-specific targeting
     * @return {string} url path
     */
    _getMilbAudioLink(platform = null) {
        const _teamId = this._teamId;
        if (this._appView !== 'web') {
            return `${this._milbMobileHostName}/gdaudio/${platform || 'iphone'}`;
        }
        return `${this._milbHostName}/milb/multimedia/audio.jsp?sid=t${_teamId}`;
    }

    /**
     * Private method called by getTvLink(), getTvLinkOrTvSubscriptionLink()
     * @param statsapiGame A Statsapi game node returned by, e.g., the Schedule API
     * @param {boolean} currentLanguageOnly True to match the user's language, false to try to match it but return
     *    a different language if user's language is not found. Optional; default is false.
     * @param returnSubLinkIfNoMedia True to return subscription link if no media found, false to return null
     * @return {string} An MLB TV URL if media found; otherwise returns null or a subscription link based on value
     *    of returnSubLinkIfNoMedia
     * @private
     */
    _getMlbTvLink(statsapiGame, currentLanguageOnly, returnSubLinkIfNoMedia, slugOption) {

        BamArgValidator.validateArgs(arguments, ['statsapiGame', 'currentLanguageOnly', 'returnSubLinkIfNoMedia', 'slugOption'],
            ['object', 'boolean', 'boolean', 'boolean'], [true, true, true, false]);

        let url = this._getMediaLink(statsapiGame, 'MLBTV', true, slugOption);

        if (!url && !currentLanguageOnly) {
            url = this._getMediaLink(statsapiGame, 'MLBTV', false, slugOption);
        }


        let hasFacebookLink = this.getFacebookGameLink(statsapiGame);
        let hasYouTubeLink = this.getYouTubeGameLink(statsapiGame);

        if (url === null && returnSubLinkIfNoMedia && !hasFacebookLink && !hasYouTubeLink) {

            url = 'https://www.mlb.com/tv';
            url = this.legacyLinkInsertLang(url);
        }

        return url;
    }

    /**
     * Private method to return milb.tv link
     * @param {object} statsapiGame Statsapi game node returned by, e.g., the Schedule API
     * @param {boolean} liveStream boolean that signals whether this game is scheduled for streaming
     * @return {string} url path
     */
    _getMilbTvLink(statsapiGame, liveStream) {
        const gamePk = statsapiGame.gamePk;
        const season = statsapiGame.season;
        let tvURL = '';

        // no milb.tv links before 2019
        if (parseInt(season, 10) > 2018) {
            // check that game is scheduled for streaming unless we're asked only for live streams
            if (statsapiGame.broadcasts && (!liveStream)) {
                let b;
                for (b = 0; b < statsapiGame.broadcasts.length; ++b) {
                    if (statsapiGame.broadcasts[b].id === 1765) {
                        // TODO: ADD DATE TO URL WHEN AWS GRID HAS FUTURE GAMES ON GRID AND DATE ROUTE
                        tvURL = '/live-stream-games';
                    }
                }
            }

            // check media states
            if (statsapiGame.content) {
                let mediaOnOrArchive = false;
                let epg = statsapiGame.content.media && statsapiGame.content.media.epg || [];
                if (!Array.isArray(epg)) {
                    epg = [epg];
                }
                const hasMedia = epg.some(function (epgItem) {
                    if (epgItem.title === 'MiLBTV' && epgItem.items.length) {
                        return epgItem.items.some(function (item) {
                            mediaOnOrArchive = item.mediaState === 'MEDIA_ON' || item.mediaState === 'MEDIA_ARCHIVE';
                            // TODO UPDATE WITH ADDITIONAL MEDIA STATES WHEN THEY ARE ADDED
                            return mediaOnOrArchive;
                        });
                    }
                    return false;
                });
                if (hasMedia) {
                    tvURL = `/live-stream-games/g${gamePk}`;
                }
            }
        }

        if (tvURL.length > 0) {
            return this.getLink(tvURL, false);
        } else {
            return '';
        }

    }

    /**
     * @method getTvLink
     * @param statsapiGame A Statsapi game node returned by, e.g., the Schedule API
     * @param {boolean} currentLanguageOnly True to match the user's language, false to try to match it but return
     *    a different language if user's language is not found. Optional; default is false. In MiLB context, this
     *    param is == liveStream. See _getMilbTvLink for info about this parameter
     * @return An MLB TV URL, or null if none found
     */
    getTvLink(statsapiGame, currentLanguageOnly) {
        return this._isMilb ? this._getMilbTvLink(statsapiGame, currentLanguageOnly) : this._getMlbTvLink(statsapiGame, !(!currentLanguageOnly), false, true);
    }

    /**
     * @method getNewTvLink
     * @param statsapiGame A Statsapi game node returned by, e.g., the Schedule API
     * @param {boolean} currentLanguageOnly True to match the user's language, false to try to match it but return
     *    a different language if user's language is not found. Optional; default is false.
     * @return An MLB TV URL without team slug, or null if none found
     */
    getNewTvLink(statsapiGame, currentLanguageOnly) {
        return this._getMlbTvLink(statsapiGame, !(!currentLanguageOnly), false, false);
    }
    /**
     * Return a mlb.tv device deep link, otherwise null
     * @param statsapiGame
     * @return {string}
     */
    getDeviceTvDeepLink(statsapiGame) {

        BamArgValidator.validateStatsapiGame(statsapiGame);

        if (statsapiGame.gamePk) {
            const hasTvLink = this.getNewTvLink(statsapiGame);

            if (hasTvLink) {
                const date = statsapiGame.officialDate && statsapiGame.officialDate.replace(/-/g, '');

                return `mlbatbat://mlbtv?gamepk=${statsapiGame.gamePk}${date ? `&date=${date}` : ''}`;
            }
        }

        return null;
    }

    /**
     * @method getTvLinkOrTvSubscriptionLink
     * @param statsapiGame A Statsapi game node returned by, e.g., the Schedule API
     * @param {boolean} currentLanguageOnly True to match the user's language, false to try to match it but return
     *    a different language if user's language is not found. Optional; default is false.
     * @return An MLB TV URL, or a subscription link if none found
     */
    getTvLinkOrTvSubscriptionLink(statsapiGame, currentLanguageOnly) {
        return this._getMlbTvLink(statsapiGame, !(!currentLanguageOnly), true);
    }

    /**
     * Generate a video-highlights link for the specified game, e.g. http://m.mlb.com/video/?game_pk=448635
     * @param statsapiGame A Statsapi game node returned by, e.g., the Schedule API
     * @return {string} A highlights link.
     */
    getVideoHighlightsLink(statsapiGame) {
        BamArgValidator.validateStatsapiGame(statsapiGame);

        let { content = {} } = statsapiGame;
        let url = null;

        if (content.summary) {

            let { hasHighlightsVideo } = content.summary;

            if (hasHighlightsVideo) {

                url = this.getLink(`/gameday/${statsapiGame.gamePk}/final/video`, false);

            }

        } else if (content.highlights) {

            if (Object.keys(content.highlights).length > 0) {

                url = this.getLink(`/gameday/${statsapiGame.gamePk}/final/video`, false);

            }

        }

        url = this.legacyLinkInsertLang(url);

        return url;

    }


    /**
     * Generate a VTP link for the specified MLB or MiLB content id, e.g. http://m.mlb.com/video/c-100018516561
     * @param videoId An id for the game
     * @return {string} A video link.
     */
    getVideoLink(videoId) {

        return this._isMilb ? `${this._milbHostName}/r/video?content_id=${videoId}` : '/video/c-' + videoId;

    }


    /**
     * Generate a VTP link for the specified topic id and a video id, e.g. http://m.mlb.com/video/t-239279104/c-100018516561
     * @param videoId An id for the game
     * @return {string} A video link.
     */
    getLiveLookIn(videoId) {

        return '/video/t-239279104/c-' + videoId;

    }

    /**
     * Returns a player-page link based on a statsapi person node.  As the player page has not been migrated to BANE
     * yet, the link is fully qualified, to the "v2" player-page implementation.
     * @param {object} statsapiPerson A statsapi person node
     * @param {string | number} teamId The player's team ID. tricode.  Optional; the returned URL will have a
     *   generic (non-team-specific) hostname if this is not supplied.
     * @param {string} platform Optional platform string (ios, android) for device-specific targeting
     * @return {string} The player-page link with a slug like 'first-last-12345678'
     */
    getPlayerPageLink(statsapiPerson, teamId, platform = null) {
        let url;
        if (this._isMilb) {
            const _teamId = teamId || this._teamId;
            if (this._appView !== 'web') {
                return `${this._milbHostName}/${this._appView}/player/${statsapiPerson.id}`;
            }
            url = `${this._milbHostName}/player/${statsapiPerson.id}`;
        } else {
            // We call the super here, but this subclass uses options to override how player slugs are derived:
            url = super.getPlayerPageLink(statsapiPerson, teamId);
        }

        return url;
    }

    /**
     * Milb specific method to returns a player-page link based on player ID and team ID.
     * @param {object} leader The player's leader object.
     * @return {string} The player-page link for example: http://www.milb.com/player/index.jsp?sid=t234&player_id=642715
     */
    getPlayerPageLinkById(leader) {
        const complexLeague = [121, 124, 130]; // DIGIT-1409 different player link on Complex leagues.
        const leagueId = leader.league.id;
        let playerPageLink = '';
        if (leader) {
            const sid = leagueId && complexLeague.indexOf(leagueId) !== -1 ? 'milb' : `t${leader.team.id}`;
            playerPageLink = `${this._milbHostName}/player/index.jsp?sid=${sid}&player_id=${leader.person.id}`;
        }
        return playerPageLink;
    }

    /**
     * Returns a player-page link based on a full name, player ID and optional team ID.  As the player page has not
     * been migrated to BANE yet, the link is fully qualified, to the "v2" player-page implementation.
     * @param fullname e.g. John Smith; required
     * @param playerId Numeric player ID; required
     * @param {string | number} teamId The player's team ID. tricode.  Optional; the returned URL will have a
     *   generic (non-team-specific) hostname if this is not supplied.
     * @return {string} The player-page link with a slug like 'first-last-12345678'
     */
    getPlayerPageLinkByNameAndId(fullname, playerId, teamId) {
        let url = super.getPlayerPageLinkByNameAndId(fullname, playerId, teamId);

        return url;
    }

    /**
     * Returns a player slug such as '12345678/first-last'.  Note that this is not really a "slug" as we normally
     * think of it; the "slug" here would be "first-last" or, if in typical BANE format, "first-last-12345678".  But
     * this method is used to build player-page links, and those still live on the "v2" site.
     * @param {object} statsapiPerson A statsapi person
     * @return {string}
     */
    getPlayerSlug(statsapiPerson) {
        return super.getPlayerSlug(statsapiPerson);
    }


    /**
     * Returns a team-homepage link based on the specified team and whether or not we are responding to a mobile
     * request (desktop homepage format is http://newyork.yankees.mlb.com/index.jsp?c_id=nyy; mobile homepage format
     * is http://m.yankees.mlb.com/).  _Once our team homepages are BANE-ified, this method's implementation will be
     * changed to reflect that (thus, code changes won't be necessary)._
     * @param {number | string} teamId Numeric team id, as a number or string
     * @return {string} The link, or null if the team is not recognized as an active team
     */
    getTeamHomepageLinkFromTeamId(teamId) {
        return super.getTeamHomepageLinkFromTeamId(teamId);
    }

    /**
     * Returns a team-schedule link based on the specified team.
     * @param {number | string} teamId Numeric team id, as a number or string
     * @return {string} The link, or null if the team is not recognized as an active team
     */
    getTeamScheduleLinkFromTeamId(teamId) {
        return super.getTeamScheduleLinkFromTeamId(teamId);
    }

    /**
     * Returns a team-homepage link based on the specified team and whether or not we are responding to a mobile
     * request (desktop homepage format is http://newyork.yankees.mlb.com/index.jsp?c_id=nyy; mobile homepage format
     * is http://m.yankees.mlb.com/).  _Once our team homepages are BANE-ified, this method's implementation will be
     * changed to reflect that (thus, code changes won't be necessary)._
     * @param {object} statsapiTeam A statsapi team node, such as returned the Sports Data Teams or Schedule API
     * @return {string} The link, or null if the team is not recognized as an active team
     */
    getTeamHomepageLink(statsapiTeam) {
        return super.getTeamHomepageLink(statsapiTeam);
    }

    /**
     * Returns a team slug such as "yankees"
     * @param {string | number} teamId A numeric team ID, as a string or number
     * @return {string} A team slug, or null if this is not a recognized active team.
     */
    getTeamSlugFromTeamId(teamId) {
        return super.getTeamSlugFromTeamId(teamId);
    }


    /**
     * Returns a team slug such as "yankees" based on the statsapi team node
     * @param {object} statsapiTeam A statsapi team node, such as returned the Sports Data Teams or Schedule API
     * @return {string} The team slug, or null if the team is not recognized as an active team
     */
    getTeamSlug(statsapiTeam) {
        return super.getTeamSlug(statsapiTeam);
    }


    /**
     * Return the fully qualified URL of the site-core static-assets root, e.g.
     * //www.mlbstatic.com/mlb.com/builds/site-core/fe87f5c56891104097b778aa06b60f1a369ec4fd_1494355291.  The returned
     * URL will not have a trailing slash.  Strings starting with '/images', '/fonts', '/bower_components', '/scripts',
     * etc. are expected to be appended to the returned URL.  Usage:
     *
     * var imageUrl = linker.getFqPathToSiteCoreAssets() + '/images/globallyUsedImage.png';
     *
     * Note that this method cannot be used to reference section-specific assets; these URLs have a different
     * structure, and also contain different build versions.  For that, use getFqPathToSectionAssets().
     *
     * @param app Server-side only: the "app" variable available to all MLB BANE controllers. Required.
     * @return {string}
     */
    getFqPathToSiteCoreAssets(app) {
        if (this._clientSide) {
            return Helpers._getMetaTagContent(this._metaTags, 'siteAssetsUri');
        }
        // We're in a controller, so:
        return this._windowOrReq.res.locals.siteAssetsUri;
    }

    /**
     * Return the fully qualified URL of the current section's static-assets root, e.g.
     * //www.mlbstatic.com/mlb.com/sections/club-schedule/builds/c47203dcaf2560531a98f3471b9972f1b9e6f492_1494356470.
     * The returned URL will not have a trailing slash.  Strings starting with '/images' or any other directories your
     * section exposes in its section_config.json are expected to be appended to the returned URL.  Usage:
     *
     * var imageUrl = linker.getFqPathToSectionAssets() + '/images/sectionSpecificImage.png';
     *
     * Note that this method cannot be used to reference global (site-core) assets; these URLs have a different
     * structure, and also contain different build versions.  For that, use getFqPathToSiteCoreAssets().
     *
     * @return {string}
     */
    getFqPathToSectionAssets() {
        if (this._clientSide) {
            return Helpers._getMetaTagContent(this._metaTags, 'sectionAssetsUri');
        }
        // We're in a controller, so:
        return this._windowOrReq.res.locals.sectionAssetsUri;
    }

    /**
     * Generate a link to the sortable stats page with a stat type and sort column, and optional team id and optional club view. http://mlb.mlb.com/stats/sortable.jsp#statType=hitting&sortColumn=avg
     * @param statType The tab to be selected on the stats page, e.g. 'hitting'
     * @param sortColumn The initial column to be sorted on when the stats page loads
     * @param siteType 'jsp' or 'v2' - default is 'jsp', pass 'v2' for http://m.mlb.com/stats page
     * @param teamId default is '' for all MLB
     * @param {boolean} keepTeam default is false. This allows the page to be seen in the club's styles. keepTeam in getLegacyLink() doesn't work for the jsp sortable stats page, so it has to be set here instead.
     * @return {string} A link to the sortable stats page.
     */
    getSortableStatsLink(statType = 'hitting', sortColumn = 'avg', siteType = 'jsp', teamId = '', keepTeam = false) {
        let teamParam = '';

        if (siteType === 'jsp') {
            const sortablePage = (this._langCode !== 'en') ? `sortable_${this._langCode}` : 'sortable';
            if (teamId) {
                let tricode = this._teamData.getTeamTricodeFromId && this._teamData.getTeamTricodeFromId(teamId);
                teamParam = `&team_id=${teamId}`;
                if (keepTeam) {
                    return this.getLegacyLink(`/stats/${sortablePage}.jsp?c_id=${tricode}#statType=${statType}&sortColumn=${sortColumn}${teamParam}`, false, siteType);
                } else {
                    return this.getLegacyLink(`/stats/${sortablePage}.jsp#statType=${statType}&sortColumn=${sortColumn}${teamParam}`, false, siteType);
                }
            } else {
                return this.getLegacyLink(`/stats/${sortablePage}.jsp#statType=${statType}&sortColumn=${sortColumn}`, keepTeam, siteType);
            }
        } else {
            const langParam = (this._langCode !== 'en') ? `&lang=${this._langCode}` : '';
            if (teamId) {
                teamParam = `&team_option=${teamId}`;
                return this.getLegacyLink(`/stats/?stat_type=${statType}&sort_column=${sortColumn}${teamParam}${langParam}`, keepTeam, siteType);
            } else {
                return this.getLegacyLink(`/stats/?stat_type=${statType}&sort_column=${sortColumn}${langParam}`, false, siteType);
            }
        }
    }

    /**
     * Generate a link to the mlb stats or milb club stats page with.  https://www.mlb.com/stats/home-runs, https://www.mlb.com/yankees/stats/home-runs
     * We expect two different sets of arguments based on the current context and destructure the
     * arguments and assign them accordingly. We opt to not extend to a separate function for milb
     * in order to preserve functionality across all of the packages that are already relying
     * on this public method.
     * @param {object} statsapiGame MiLB: A SD API game node.
     * @param {string} sId Season id
     * @param {string} statType MLB: The tab to be selected on the stats page, default to 'hitting'
     * @param {string} sortColumn MLB: The initial column to be sorted on when the stats page loads
     * @return {string} A link to the mlb or club stats page.
     */
    getStatsLink(...args) {
        if (this._isMilb) {
            const [statsapiGame, sId] = args;
            const gid = _getGID(statsapiGame, this);
            const siteId = sId || this._siteId;
            const host = this._appView === 'web' ? this._milbHostName : this._milbMobileHostName;
            return `${host}/milb/stats/stats.jsp?gid=${gid}&t=g_rcp&sid=${siteId}`;
        }

        const [cfg = {}] = args;
        const { sortColumn = '', statType = ''} = cfg;
        const url = this.getLink('/stats');
        const type = (statType && statType === 'pitching') ? '/pitching' : '';
        const column = (sortColumn) ? `/${sortColumn}` : '';

        return `${url}${type}${column}`;
    }

    /**
     * @method getFacebookGameLink
     * @param statsapiGame A Statsapi game node returned by, e.g., the Schedule API
     * @return A Facebook Game of the Week URL, or null if none found
     */
    getFacebookGameLink(statsapiGame) {
        return this._getGOWLink(statsapiGame, 'facebook');
    }


    /**
     * @method getYouTubeGameLink
     * @param statsapiGame A Statsapi game node returned by, e.g., the Schedule API
     * @return A YouTube Game of the Week URL, or null if none found
     */
    getYouTubeGameLink(statsapiGame) {
        return this._getGOWLink(statsapiGame, 'youtube');
    }


    /**
     * @method getContactUsLink
     * @return The contact us url
     */
    getContactUsLink() {
        return this.getLink('/official-information/contact', false);
    }

    /**
     * Return a standings link
     * @param {number} leagueId League id
     * @return {string} url path
     */
    getStandingsLink(leagueId) {
        return `${this._milbHostName}/standings/index.jsp?lid=${leagueId || this._leagueId}`;
    }

    /**
     * Return schedule url maintaining team/league context if applicable
     * @return {string} url path
     */
    getScheduleLink() {
        return this.getLink('/schedule', true);
    }

    /**
     * Return tunein link
     * @param {object} statsapiGame A SD API game node
     * @return {string} url path
     */
    getTuneInLinkForContextTeam(statsapiGame) {
        return _getTuneInLink(statsapiGame, this, 'contextOnly');
    }

    /**
     * Return tune-in link for MiLB
     * @param {object} statsapiGame A SD API game node
     * @param {string} homeOrAway String that denotes whether team is home or away team
     * @return {string} url path
     */
    getTuneInLink(statsapiGame, homeOrAway = 'any') {
        return _getTuneInLink(statsapiGame, this, homeOrAway);
    }

    /**
     * @method getYouTubeGameLink
     * @param statsapiGame A Statsapi game node returned by, e.g., the Schedule API
     * @return A YouTube Game of the Week URL, or null if none found
     */
    _getGOWLink(statsapiGame, type) {

        let gameLink = null;

        if (statsapiGame.content && statsapiGame.content.media && statsapiGame.content.media.epg) {
            let epgs = statsapiGame.content.media.epg;
            let epg;
            let items;
            let item;

            for (let eIdx = 0, eLength = epgs.length; eIdx < eLength; eIdx++) {

                epg = epgs[eIdx];

                if (epg.title.toLowerCase() === 'mlbtv' && epg.items) {

                    items = epg.items;
                    for (let iIdx = 0, iLength = items.length; iIdx < iLength; iIdx++) {
                        item = items[iIdx];

                        const isYouTubeOn = (type === 'youtube' && item.youtube && item.youtube.fgow && item.mediaState === 'MEDIA_ON');
                        const isFacenbookOn = (type === 'facebook' && item.facebook && item.facebook.fgow && item.mediaState === 'MEDIA_ON');

                        if (isYouTubeOn) {
                            gameLink = statsapiGame.youtubeMLBLink || 'https://www.mlb.com/live-stream-games/youtube';
                            return gameLink;

                        }

                        if (isFacenbookOn) {
                            let videoId = item.facebook.videoId || '';
                            let pageId = item.facebook.pageId || '';

                            if (videoId && pageId) {
                                gameLink = `https://www.facebook.com/${pageId}/videos/${videoId}`;
                                return gameLink;
                            }
                        }
                    }
                }
            }
        }

        // Provide override url for youtube playback on wbc games
        // Scope to sportId 51, and apply to youtube fgow inspection.
        // Should only apply if above match is not met.
        if (type === 'youtube' && statsapiGame.teams && statsapiGame.teams.home && statsapiGame.teams.home.team && statsapiGame.teams.home.team.sport && statsapiGame.teams.home.team.sport.id === 51) {
            if (statsapiGame.xrefIds && Array.isArray(statsapiGame.xrefIds)) {
                statsapiGame.xrefIds.forEach(function (xrefId) {
                    if (xrefId && xrefId.xrefType === 'bam_youtube_free') {
                        gameLink = 'https://youtu.be' + xrefId.xrefId;
                    }
                });
            }
        }

        return gameLink;

    }

    /**
     * For legacy links where alternate language is supported, insert language code into url in appropriate place
     * @param url {String}
     * @returns {string} URL with lang code (ie: 'http://m.es.mlb.com/video/?game_pk=509714' when
     * 'http://m.mlb.com/video/?game_pk=509714' is passed in)
     */
    legacyLinkInsertLang(url = null) {

        let retUrl = url;

        if (url) {

            if (this._langCode !== 'en') {

                let isV2 = (url.indexOf('m.mlb.com') !== -1);

                if (isV2) {

                    retUrl = retUrl.replace('m.mlb.com', `m.${this._langCode}.mlb.com`);

                } else { // jsp

                    retUrl = retUrl.replace('mlb.com/', `mlb.com/${this._langCode}/`);

                }

            }

        }

        return retUrl;

    }

    /**
     * Passed the ID of a team (where the team is an active MLB team or a "TBD team" used as a placeholder for an
     * unknown team during postseason), returns the fully qualified URL of the logo to display for this team (a team
     * logo in the case of an active team; an AL or NL logo in the case of most TBD teams; or a generic baseball logo
     * in the case of two special-case TBD teams as well as any input we can't otherwise parse).
     *
     * @param {number|string} teamId The numeric ID of an active team or a postseason "TBD team"
     * @param {Object} options Options (optional) describing logo variation; two properties, both optional, are:
     * `logoType` ("cap" (the default), "primary", or "wordmark"); and `onLight` (true, the default, for the "-light"
     * variant, false for the "-dark" variant).  Note: `logoType` is ignored for league logos and for the generic
     * baseball logo.  Also note: as of 9/11/2017, there is no "-dark" variant of the league logos.  This method allows
     * the user to specify onLight = false ("-dark"), however, because presumably this resource will exist at some
     * point.
     * @return {string} A fully qualified URL to an SVG logo.
     */
    getFqLogoUrlFromTeamId(teamId, options = {logoType: 'cap', onLight: true}) {
        let logoType = options.logoType || 'cap';
        let onLight = typeof options.onLight === 'boolean' ? options.onLight : true;
        let teamIdNum = parseInt(teamId);
        if (isNaN(teamIdNum)) {
            teamIdNum = 0;
        }

        let isActiveTeam = this.getBamTeamData().isTeamActive(teamIdNum);

        // SCENARIO 1: ACTIVE TEAM:
        if (isActiveTeam) {
            return this._siteCoreLogosRoot + 'team-' + logoType + '-on-' + (onLight ? 'light' : 'dark')
                + '/' + teamId + '.svg';
        }

        // SCENARIO 2: ASG TEAM:
        if (teamIdNum === AL_ASG_TEAM_ID || teamIdNum === NL_ASG_TEAM_ID) {
            return this.getFqLogoUrlFromLeagueId(teamIdNum);
        }

        const leagueInfo = MlbTbdTeamToLeagueMap.getLeagueInfoFromTbdTeamId(teamId);
        // SCENARIO 3: TBD TEAM WITH A KNOWN LEAGUE:
        if (!leagueInfo.unknownLeague) {
            return this.getFqLogoUrlFromLeagueId(leagueInfo.leagueId, onLight);
        }

        // SCENARIO 4: A VALID TBD TEAM WITHOUT A KNOWN LEAGUE, OR WE COULDN'T PARSE TEAMID:
        // (note: we use "cap" regardless of options; this logo only exists in "cap" format):
        return this.getFqGenericBaseballLogo(onLight);
    }

    /**
     * Returns the fully qualified URL to either the AL or NL logos.
     * @param {number|string} leagueId (AL = 103, NL = 104)
     * @param {boolean} onLight True for light-background version, false for dark-background version BUT NOTE: THERE ARE
     * NO DARK-BACKGROUND LEAGUE LOGOS AS OF 9/12/2017); default is true
     * @return {string}
     */
    getFqLogoUrlFromLeagueId(leagueId, onLight = true) {
        const leagueIdNum = (parseInt(leagueId) || 0);
        let leagueIdAsTeam;
        switch (leagueIdNum) {
            // Note: no break stmts or braces needed here since in each case we return immediately:
            case AL_ASG_TEAM_ID:
            case NL_ASG_TEAM_ID:
                return this._siteCoreLogosRoot
                + 'team-cap-on-' + (onLight ? 'light' : 'dark') + '/' + leagueIdNum + '.svg';
            case AL_ID:
            case NL_ID:
                leagueIdAsTeam = leagueIdNum === AL_ID ? AL_ASG_TEAM_ID : NL_ASG_TEAM_ID;
                return this._siteCoreLogosRoot
                + 'team-cap-on-' + (onLight ? 'light' : 'dark') + '/' + leagueIdAsTeam + '.svg';
            default:
                return this.getFqGenericBaseballLogo(onLight);
        }
    }

    /**
     * Returns the fully qualified URL to either the AL or NL logos (passed an unrecognized leagueAbbrev, returns
     * the generic baseball logo).
     * @param {string} leagueAbbrev 'AL' or 'NL'
     * @param {boolean} onLight True for light-background version, false for dark-background version BUT NOTE: THERE ARE
     * NO DARK-BACKGROUND LEAGUE LOGOS AS OF 9/12/2017); default is true
     * @return {string}
     */
    getFqLogoUrlFromLeagueAbbrev(leagueAbbrev, onLight = true) {
        switch (leagueAbbrev) {
            // Note: no break stmts or braces needed here since in each case we return immediately:
            case 'AL': case 'NL': return this.getFqLogoUrlFromLeagueId({AL: AL_ID, NL: NL_ID}[leagueAbbrev],
                onLight);
            default: return this.getFqGenericBaseballLogo(onLight);
        }
    }

    /**
     * Returns the fully qualified URL of 000.svg, the generic baseball logo generally used when we don't have a logo
     * for a team for whatever reason.
     * @param {boolean} onLight True for light-background version, false for dark-background version (they are identical
     *      as of 9/12/2017); default is true
     * @return {string}
     */
    getFqGenericBaseballLogo(onLight = true) {
        return this._siteCoreLogosRoot + 'team-cap-on-' + (onLight ? 'light' : 'dark') + '/' + '000.svg';
    }

    /**
     * Returns the fully qualified URL of 000.svg, the generic baseball logo generally used when we don't have a logo
     * for a team for whatever reason.
     * @param {boolean} onLight True for light-background version, false for dark-background version (they are identical
     *      as of 9/12/2017); default is true
     * @return {string}
     */
    getFqWBCLeagueLogo(onLight = true) {
        return this._siteCoreLogosRoot + 'team-cap-on-' + (onLight ? 'light' : 'dark') + '/' + '000.svg';
    }

    /**
     * Passed a legacy MLB URL (e.g. to an image) which is http://, not https://, returns the https:// version if
     * it can.  Passed a URL which already starts with https://, returns the URL unchanged.  Passed a relative-to-root
     * url like /images/some/img.jpg, prepends https://securea.mlb.com.
     *
     * Specifics:
     *
     * - Passed an http://mlb.mlb.com URL, returns an https://securea.mlb.com URL
     * - Passed an http://m.mlb.com , returns an https://msecurea.mlb.com URL
     * - Passed e.g. /images/some/img.jpg, returns https://securea.mlb.com/images/some/img.jpg
     *
     * @param {string} url
     * @param {boolean} returnInsecureIfNoMatch If true and no match is found, url will be returned (otherwise null
     *   will be returned).  Optional; default is true.  Note: if the passed URL is relative-to-root (/some/path, with
     *   no hostname or protocol), a secure URL will always be returned; thus this param will have no effect).
     * @returns {string} A fully qualified https:// URL if it can, otherwise null (if returnSecureIfNoMatch is false)
     *   or url (if returnInsecureIfNoMatch is true)
     * @throws Error if URL doesn't start with http:// or https:// or /.
     */
    static getSecureLink(url, returnInsecureIfNoMatch) {
        returnInsecureIfNoMatch = (returnInsecureIfNoMatch === undefined ? true : returnInsecureIfNoMatch);
        let hosts = [
            {insecure: 'mlb.mlb.com', secure: 'securea.mlb.com'},
            {insecure: 'm.mlb.com', secure: 'msecurea.mlb.com'}
        ];
        if (url.indexOf('https://') === 0) {
            // already secure:
            return url;
        } else if (url.indexOf('http://') === 0) {
            // An FQ, non-secure URL:
            let host;
            let replacementCandidate;
            for (let i = 0, ln = hosts.length; i < ln; i++) {
                host = hosts[i];
                replacementCandidate = 'http://' + host.insecure;
                if (url.indexOf(replacementCandidate) === 0) {
                    return 'https://' + host.secure + url.substring(replacementCandidate.length);
                }
            }
            // no luck:
            return returnInsecureIfNoMatch ? url : null;
        } else if (url.indexOf('/') === 0) {
            return 'https://' + hosts[0].secure + url; // https://securea.mlb.com
        } else {
            throw new Error('the url "' + url + '" is not fully qualified, nor does it start with "/"; one of these' +
                'is required.');
        }
    }

    /**
     * For some legacy URLs, this class returns localized URLs containing e.g. "/es" which we don't want; this static
     * method returns the passed URL without it if it's present.  Also removes "/en" if present (unlikely).
     * @param url
     * @return The passed URL with the language path element removed if present (e.g. https://host/path when passed
     * https://host/es/path).
     */
    static stripLanguagePathElement(url) {
        let retUrl = url;
        const langRegexes = [/\/es\//, /\/en\//];
        for (let i = 0, ln = langRegexes.length; i < ln; i++) {
            retUrl = retUrl.replace(langRegexes[i], '/');
        }
        return retUrl;
    }

}

/**
 * Return the specified ticket link if available, otherwise null
 * @param statsapiGame A SD API game node
 * @param forceDesktop If true, only desktop links will be returned; if false, mobile ones will be returned as well
 * @param linker the MlbLinker instance calling this function
 * @param sellerType Which type of seller's link is okay to return?  Three possible values:
 *          - 'contextOnly': only return link to seller associated with the context team
 *          - 'opponentOnly': only return link to seller asociated with the opponent
 *          - 'any' expected to be called only when there isn't a context (i.e., the league site), returns the home
 *            team's seller's link if it exists; otherwise returns the away team's seller's link
 *
 * @return {string} A URL or null
 * @private
 */
function _getTicketsLink(statsapiGame, forceDesktop, linker, sellerType, nonGameEvent) {
    if (!nonGameEvent) {
        BamArgValidator.validateStatsapiGame(statsapiGame);
        BamArgValidator.validateArgs(arguments, ['statsapiGame', 'forceDesktop', 'linker', 'sellerType'],
            ['object', 'boolean', 'object', 'string'], [true, true, true, true]);
    } else {
        BamArgValidator.validateStatsapiNonGameEvent(statsapiGame);
    }
    BamArgValidator.validateAllowed(sellerType, ['any', 'contextOnly', 'opponentOnly']);

    if (sellerType !== 'any' && !linker._teamId) {
        throw new Error('You called _getTicketsLink() with sellerType="' + sellerType + '"; this type requires ' +
            'a team context, but there is none (because you are not on a club site).');
    }

    if (linker._isMilb && (statsapiGame.status && statsapiGame.status.abstractGameCode === 'F')) {
        return null; // Return null in milb context for final games
    }

    if (statsapiGame.tickets) {
        let url = null;
        const awayId = !nonGameEvent
                ? statsapiGame.teams.away.team.id
                : (statsapiGame.teams[0] && statsapiGame.teams[0].id);
        let homeOrAway = +linker._teamId === awayId ? 'away' : 'home';

        if (sellerType === 'any') {
            url = _getTicketsLinkOfType(statsapiGame.tickets, 'home', linker)
                || _getTicketsLinkOfType(statsapiGame.tickets, 'away', linker);
        } else {
            homeOrAway = (sellerType === 'contextOnly')
                ? (awayId === +linker._teamId ? 'away' : 'home') // Ensure these values are numbers
                : (awayId !== +linker._teamId ? 'away' : 'home');

            if (linker._isMilb && nonGameEvent) {
                homeOrAway = 'home';
            }

            url = _getTicketsLinkOfType(statsapiGame.tickets, homeOrAway, linker);
        }

        if (url !== null) {
            return url;
        }
    }

    if (!forceDesktop && linker._deviceType !== 'desktop') {
        // Mobile user; no mobile tix link so try for desktop - if no luck on the second try, we'll return null
        // because we'll get to this if clause but not into it:
        return _getTicketsLink(statsapiGame, true, linker, sellerType);
    }
    return null;
}

/**
 * Returns a ticket link based on the specified type (home or away) and other criteria
 * @param ticketsArr Array of
 * @param homeOrAway
 * @param linker an MlbLinker instance
 * @private
 */
function _getTicketsLinkOfType(ticketsArr, homeOrAway, linker) {
    let ticketType = (linker._deviceType === 'mobile' ? 'mobile' : 'wired');
    let ticketEl;
    for (let i = 0, ln = ticketsArr.length; i < ln; i++) {
        ticketEl = ticketsArr[i];
        if (ticketEl.ticketType && ticketEl.ticketType === ticketType) {
            if (ticketEl.ticketLinks && ticketEl.ticketLinks[homeOrAway]) {
                return ticketEl.ticketLinks[homeOrAway];
            }
        }
    }
    return null;
}

/**
 * Return the specified TuneIn audio link if available, otherwise null
 * @param statsapiGame A SD API game node
 * @param linker the MlbLinker instance calling this function
 * @param homeAwayContext Which team's audio is okay to return?  Three possible values:
 *          - 'contextOnly': only return audio associated with the context team
 *          - 'home': only return audio asociated with the home team
 *          - 'away': only return audio asociated with the away team
 *          - 'any' expected to be called only when there isn't a context (i.e., the league site), returns the home
 *            team's audio link if it exists; otherwise returns the away team's audio link
 *
 * @return {string} A URL or null
 * @private
 */
function _getTuneInLink(statsapiGame, linker, homeAwayContext) {
    BamArgValidator.validateStatsapiGame(statsapiGame);
    BamArgValidator.validateArgs(arguments, ['statsapiGame', 'linker', 'homeAwayContext'],
        ['object', 'object', 'string'], [true, true, true, true]);
    BamArgValidator.validateAllowed(homeAwayContext, ['contextOnly', 'home', 'away', 'any']);

    let url = null;
    if (statsapiGame.broadcasts) {
        let showHome = homeAwayContext === 'home';
        let showAway = homeAwayContext === 'away';

        if (homeAwayContext === 'contextOnly') {
            const awayId = statsapiGame.teams.away.team.id;
            showHome = linker._teamId !== awayId;
            showAway = linker._teamId === awayId;
        }
        if (homeAwayContext === 'any') {
            showHome = true;
            showAway = true;
        }

        let b;
        for (b = 0; b < statsapiGame.broadcasts.length; ++b) {
            const broadcast = statsapiGame.broadcasts[b] || {};
            if (broadcast.type === 'INET') {

                if (showHome && broadcast.homeAway === 'home') {
                    return broadcast.sourceUrl || linker.getLink('live-stream-games/audio', false);
                }
                if (showAway && broadcast.homeAway === 'away') {
                    return broadcast.sourceUrl || linker.getLink('live-stream-games/audio', false);
                }
            }
        }
    }
    return url;
}

/**
 * Set milb specific properties on linker object.
 * @param {object} linker The instance of the MlbLinker that you would like to modify
 * @param {object} windowOrReq Either window or request object
 * @param {object} options Linker options
 */
function _setMilbProperties(linker, windowOrReq, options = {}) {

    let metaLeagueId;
    let metaTeamId;
    let metaSlug;
    let metaAppView;
    let metaLocale;
    linker._isMilb = true;

    if (linker._clientSide) {
        metaAppView = Helpers._getMetaTagContent(linker._metaTags, 'app-view');
        metaLocale = Helpers._getMetaTagContent(linker._metaTags, 'locale');
        metaLeagueId = Helpers._getMetaTagContent(linker._metaTags, 'league-id');
        metaTeamId = Helpers._getMetaTagContent(linker._metaTags, 'team-id');
        metaSlug = Helpers._getMetaTagContent(linker._metaTags, 'club');

    } else if (windowOrReq && windowOrReq.locals) {
        const {
            team,
            league
        } = windowOrReq.locals.globalData || {};

        const {
            appViews,
            locale,
            club
        } = windowOrReq.locals.responseConfig || {};

        linker._deviceType = options.deviceType || 'desktop';
        metaTeamId = team && team.data && team.data.team.id;
        metaLeagueId = league && league.data && league.data.league.id;
        metaAppView = appViews || '';
        metaLocale = locale || 'en';
        metaSlug = (!club) ? '' : club;
    }

    let siteId;

    if (metaTeamId) {
        siteId = `t${metaTeamId}`;
    } else if (metaLeagueId) {
        siteId = `l${metaLeagueId}`;
    } else {
        siteId = null;
    }

    if (options.langCodeOverride) {
        options.locale = options.langCodeOverride;
    }

    if (options.teamSlugs || windowOrReq.teamSlugs) {
        options.teamSlugs = options.teamSlugs || windowOrReq.teamSlugs || null;
    }

    linker._baseUrl = options.baseUrl;
    linker._milbHostName = linker._baseUrl || 'http://www.milb.com';
    linker._milbMobileHostName = linker._baseUrl || 'http://m.milb.com';
    linker._siteId = options.siteId || siteId;
    linker._teamId = +options.teamId || +metaTeamId; // set teamId as integer
    linker._slug = options.slug || metaSlug || '';
    linker._leagueId = options.leagueId || metaLeagueId;
    linker._appView = options.appView || metaAppView;
    linker._langCode = options.locale || metaLocale;

    linker._ymdDate = options.ymdDate || null;
    linker._calEventId = options.calEventId || null;
    linker._gameId = options.gameId || null;
    linker._videoId = options.videoId || null;
    linker._teamSlugs = options.teamSlugs || null;
    linker._sportCodeMap = {
        11: 'aaa',
        12: 'aax',
        13: 'afa',
        14: 'afx',
        15: 'asx',
        16: 'rok',
        17: 'win'
    };
}

/**
 * Helper function to build gameId in MiLB context if supplied the statsApiGame and the linker
 * @param {object} statsapiGame A SD API game node
 * @param {object} linker MlbLinker instance calling this function
 * @return {string} Returns GID string
 */
function _getGID(statsapiGame, linker) {
    const awayTeamCode = statsapiGame.teams.away.team.teamCode;
    const homeTeamCode = statsapiGame.teams.home.team.teamCode;
    const awaySportCode = linker._sportCodeMap[statsapiGame.teams.away.team.sport.id];
    const homeSportCode = linker._sportCodeMap[statsapiGame.teams.home.team.sport.id];
    const gameNumber = statsapiGame.gameNumber;
    let gameDate = statsapiGame.gameDate.substr(0, 10);
    gameDate = gameDate.replace(/-/g, '_');

    return `${gameDate}_${awayTeamCode}${awaySportCode}_${homeTeamCode}${homeSportCode}_${gameNumber}`;

}

export default MlbLinker;
